import { TDateField, TImageField, TLinkField, TStringField, TVideoSitecoreField } from '../../../types/sitecore.types';
import { THeadingTag } from '../../../types/types';
import { isEmptyObject } from '../../../utils/checkEmptyObject';
import { checkIsHrefValid } from '../../../utils/checkIsHrefValid.utils';
import { TSocialMediaBarProps } from '../../@shared/socialMediaBar/types/socialMediaBar.types';
import { TCategory, TNewsDetailContext, TNewsDetailFields, TNewsDetailParams, TNewsDetailProps } from '../types/newsDetail.types';

export class NewsDetailModel {
  private readonly fields?: TNewsDetailFields;
  private readonly params?: TNewsDetailParams;
  private readonly context?: TNewsDetailContext;

  constructor(props: TNewsDetailProps) {
    this.fields = props.fields;
    this.params = props.params;
    this.context = props?.sitecoreContext as TNewsDetailContext || null;
  }

  get eyebrow(): TStringField | undefined {
    return this.fields?.eyebrow;
  }

  get title(): TStringField | undefined {
    return this.fields?.title;
  }
  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h1';
  }

  get newsDate(): TDateField | undefined {
    return this.fields?.newsDate;
  }

  get contextLanguage(): string {
    return this.context?.language || 'en-GB';
  }

  get image(): TImageField | undefined {
    return this.fields?.image;
  }

  get mobileImage(): TImageField | undefined {
    return this.fields?.mobileImage;
  }

  get video(): TVideoSitecoreField | undefined {
    return this.fields?.video;
  }

  get videoUrl(): string {
    return this.fields?.video?.value.href || '';
  }

  get showVideo(): boolean {
    return this.fields?.useVideo?.value || false;
  }

  get tags(): TStringField | undefined {
    return this.fields?.tags;
  }

  get categories(): Array<TCategory> {
    if (this?.fields?.tagCategories?.length) {
      return this.fields?.tagCategories?.map((category) => {
        const redirectUrl = (this.fields?.newsMainPageLink?.value || '') + '/?' + category?.parent + '=' + category?.id;
        return {
          name: category?.name,
          href: encodeURI(redirectUrl),
        };
      });
    }
    return [];
  }

  get shortDescription(): TStringField | undefined {
    return this.fields?.shortDescription;
  }

  get detailedDescription(): TStringField | undefined {
    return this.fields?.detailedDescription;
  }
  get primaryCta(): TLinkField | undefined {
    return this.fields?.primaryCta;
  }

  get showPrimaryCta(): boolean {
    return !!(this.primaryCta?.value?.text && checkIsHrefValid(this.primaryCta?.value?.href));
  }

  get secondaryCta(): TLinkField | undefined {
    return this.fields?.secondaryCta;
  }

  get showSecondaryCta(): boolean {
    return !!(this.secondaryCta?.value?.text && checkIsHrefValid(this.secondaryCta?.value?.href));
  }

  get showCtaWrapper(): boolean {
    return this.showPrimaryCta || this.showSecondaryCta;
  }

  get hasMedia(): boolean {
    return !!((this.showVideo && this.videoUrl) || this.image?.value?.src || this.mobileImage?.value?.src);
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get socialMediaBarProps(): TSocialMediaBarProps | undefined {
    return isEmptyObject(this.fields?.socialMediaBar) ? undefined : this.fields?.socialMediaBar;
  }
}
